import { IAppContext } from 'App/AppContext';
import { apiCompleteSkillControlSignIn, apiStartSkillControlSignIn } from 'services/Api/skillControlConnectionService';
import { apiRequest } from 'services/Auth/authConfig';
import Config from 'services/Config/configService';

/** The message type the API's SkillControl callback page posts to the window that opened it. */
export const skillControlSignInMessageType = 'isoplanner-skillcontrol-signin';

/** How long the popup may stay open before the attempt is given up as abandoned. */
const signInTimeoutMs = 10 * 60 * 1000;

export type SkillControlSignInOutcome = 'connected' | 'cancelled' | 'blocked' | 'failed';

export interface ISkillControlSignInResult {
  outcome: SkillControlSignInOutcome;
  /** The authorization server's error word when it refused, for the card. */
  error?: string;
}

interface ISignInMessage {
  type?: string;
  ok?: boolean;
  error?: string;
  code?: string;
  state?: string;
}

/** Only a message from the API's origin is trusted, so a page open in another tab cannot pretend the sign-in worked. */
const getSignInOrigin = (): string => {
  try {
    return new URL(Config.get('Api.BaseURL')).origin;
  } catch {
    return '';
  }
};

/**
 * The signed-in user's own sign-in at SkillControl (phase 22, D22-1), in a popup: the API hands out SkillControl's
 * authorization page, and the API's callback page hands this window the code and the state through postMessage. This
 * window, signed in, completes the sign-in at the API, which checks that the state names this user before it exchanges
 * the code; the anonymous callback stores nothing. Resolves when the sign-in is completed, refused, abandoned, or the
 * popup could not be opened; rejects with the API's refusal of the start or the completion.
 *
 * The popup is opened BEFORE the API call: a window opened after an await is no longer inside the click and most
 * browsers block it. The blank window navigates once the url is known (the document-grant pattern).
 */
const startSignIn = async (appContext: IAppContext): Promise<ISkillControlSignInResult> => {
  const popup = window.open('', 'isoplanner-skillcontrol-signin', 'popup=yes,width=560,height=720');

  if (!popup) {
    return { outcome: 'blocked' };
  }

  let url: string;

  try {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    url = await apiStartSkillControlSignIn(accessToken);
  } catch (err) {
    popup.close();
    throw err;
  }

  if (!url) {
    popup.close();

    return { outcome: 'failed' };
  }

  popup.location.href = url;

  const expectedOrigin = getSignInOrigin();

  return new Promise<ISkillControlSignInResult>((resolve) => {
    let settled = false;

    const finish = (result: ISkillControlSignInResult | Promise<ISkillControlSignInResult>) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      window.clearInterval(closedTimer);
      window.clearTimeout(timeout);
      try {
        if (!popup.closed) popup.close();
      } catch {
        // a window on another origin may refuse; it closes itself anyway
      }
      resolve(result);
    };

    const onMessage = (event: MessageEvent) => {
      if (!expectedOrigin || event.origin !== expectedOrigin) return;
      const data = event.data as ISignInMessage | undefined;
      if (!data || data.type !== skillControlSignInMessageType) return;

      if (!data.ok || !data.code || !data.state) {
        finish({ outcome: 'failed', error: data.error });

        return;
      }

      finish(completeSignIn(appContext, data.code, data.state));
    };

    // the callback page closes itself after posting; a popup closed before that is the person giving up
    const closedTimer = window.setInterval(() => {
      if (popup.closed) {
        window.setTimeout(() => finish({ outcome: 'cancelled' }), 300);
      }
    }, 500);

    const timeout = window.setTimeout(() => finish({ outcome: 'cancelled' }), signInTimeoutMs);

    window.addEventListener('message', onMessage);
  });
};

let inFlight: Promise<ISkillControlSignInResult> | undefined;

/**
 * One sign-in per page at a time: a second click (another refusal card in the same conversation) joins the running
 * attempt instead of opening the same named popup again, which would let both listeners complete one callback.
 */
export const signInToSkillControl = (appContext: IAppContext): Promise<ISkillControlSignInResult> => {
  if (inFlight) return inFlight;

  inFlight = startSignIn(appContext).finally(() => {
    inFlight = undefined;
  });

  return inFlight;
};

const completeSignIn = async (
  appContext: IAppContext,
  code: string,
  state: string,
): Promise<ISkillControlSignInResult> => {
  const accessToken = await appContext.getAccessToken(apiRequest.scopes);
  await apiCompleteSkillControlSignIn(code, state, accessToken);

  return { outcome: 'connected' };
};
